"use client";

import { useCallback, useEffect, useMemo, useState, useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import { ActiveFilters, FacetKey } from "@/@types/filters";
import {
  countActiveFilters,
  filtersToSearchParams,
  parseFiltersFromParams,
} from "@/lib/products";

/**
 * A URL é a fonte da verdade dos filtros: o estado local existe apenas como
 * camada otimista, para o chip pintar no clique sem esperar o servidor.
 *
 * (React 18 aqui, então nada de useOptimistic — é API do 19.)
 */
export default function useProductFilters() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const searchParamsKey = searchParams.toString();

  const fromUrl = useMemo(
    () => parseFiltersFromParams(new URLSearchParams(searchParamsKey)),
    [searchParamsKey]
  );

  const [optimistic, setOptimistic] = useState<ActiveFilters>(fromUrl);

  // Reconcilia com a URL quando a navegação termina, e também quando o usuário
  // usa voltar/avançar do navegador.
  useEffect(() => {
    if (!isPending) setOptimistic(fromUrl);
  }, [fromUrl, isPending]);

  const navigate = useCallback(
    (next: ActiveFilters) => {
      setOptimistic(next);

      const params = filtersToSearchParams(next, new URLSearchParams(searchParamsKey));
      // Mexer num filtro reinicia a paginação.
      params.delete("pagina");

      const query = params.toString();

      startTransition(() => {
        router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
      });
    },
    [pathname, router, searchParamsKey]
  );

  const toggle = useCallback(
    (key: FacetKey, value: string) => {
      const current = optimistic[key];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];

      navigate({ ...optimistic, [key]: next });
    },
    [navigate, optimistic]
  );

  const remove = useCallback(
    (key: FacetKey, value: string) => {
      navigate({ ...optimistic, [key]: optimistic[key].filter((item) => item !== value) });
    },
    [navigate, optimistic]
  );

  const clear = useCallback(() => {
    navigate({ kosher_tipo: [], cor: [], material: [], especificacao: [], produto: [] });
  }, [navigate]);

  const isSelected = useCallback(
    (key: FacetKey, value: string) => optimistic[key].includes(value),
    [optimistic]
  );

  return {
    filters: optimistic,
    activeCount: countActiveFilters(optimistic),
    isPending,
    toggle,
    remove,
    clear,
    isSelected,
  };
}
